import * as THREE from "three";
import { inked, toon } from "../toon";
import { NEUTRAL_BUILD, type RiderBuild } from "../styles";
import { buildNaturalCowboyHat } from "./hat-natural";
import { buildNaturalSpaceHelmet } from "./helmet-natural";
import { buildNaturalFace, buildNaturalLongHair, buildNaturalMohawk, buildNaturalPonytail } from "./head-natural";
import { buildMohawk, buildToyFace, buildToyHatHair, buildToyHelmetHair } from "./head-toy";
import type { SkaterLook } from "./look";
import { buildToyLongHair } from "./long-hair";
import { buildToyPonytail } from "./ponytail";
import { facets } from "./geom";

// The head: face, hair and headwear (head space, face +z inside `buildHead`).
// Each face kind (`RiderBuild.face`) is one entry in `HEADS`, with its own
// face, mohawk, cowboy hat, space helmet, long hair and ponytail; the cap is shared. A new kind is
// a new entry, not a branch in each builder.

/** What one face kind builds (head space, face +z). */
export interface HeadKind {
  /** The face; `eyes` scales the eyes (1 is the cartoon's). */
  face(look: SkaterLook, into: THREE.Group, eyes: number): void;
  mohawk(color: string): THREE.Group;
  /** The cowboy hat in `color`, with `hair` showing under it. */
  cowboyHat(color: string, hair: string): THREE.Group;
  /** The space helmet in `color`, with `hair` inside it. */
  spaceHelmet(color: string, hair: string): THREE.Group;
  /** Long hair (#227): what stays on the head, and the fall down the back (`hair-swing`), on a head `size` times the toy's. */
  longHair(color: string, size: number): THREE.Group;
  /** A ponytail (#234): hair pulled back to a tie high on the back of the head, the tail swinging from it (`hair-swing`). */
  ponytail(color: string): THREE.Group;
}

export const HEADS: Readonly<Record<RiderBuild["face"], HeadKind>> = {
  toy: { face: buildToyFace, mohawk: buildMohawk, cowboyHat: buildCowboyHat, spaceHelmet: buildSpaceHelmet, longHair: buildToyLongHair, ponytail: buildToyPonytail },
  natural: {
    face: buildNaturalFace,
    mohawk: buildNaturalMohawk,
    cowboyHat: buildNaturalCowboyHat,
    spaceHelmet: buildNaturalSpaceHelmet,
    longHair: buildNaturalLongHair,
    ponytail: buildNaturalPonytail,
  },
};

export function buildHead(look: SkaterLook, head: THREE.Group, build: RiderBuild = NEUTRAL_BUILD): void {
  // Built facing +z, then turned so the face lines up with the chest (-x).
  // The build's head size scales face, hair and headwear together.
  const inner = new THREE.Group();
  inner.rotation.y = -Math.PI / 2;
  if (build.head !== 1) inner.scale.setScalar(build.head);
  head.add(inner);
  const kind = HEADS[build.face];
  kind.face(look, inner, build.eyes);
  inner.add(buildHeadwear(look, kind, build.head));
}

/** The headwear for a look; `size` is the head's, for long hair to reach the same place on the body. */
export function buildHeadwear(look: SkaterLook, kind: HeadKind = HEADS.toy, size = 1): THREE.Group {
  switch (look.hair.style) {
    case "mohawk":
      return kind.mohawk(look.hair.color);
    case "cap":
      return buildCap(look.hair.color);
    case "cowboy-hat":
      return kind.cowboyHat(look.hair.hat ?? "#7a4a24", look.hair.color);
    case "space-helmet":
      return kind.spaceHelmet(look.hair.hat ?? "#f4f6fb", look.hair.color);
    case "long":
      return kind.longHair(look.hair.color, size);
    case "ponytail":
      return kind.ponytail(look.hair.color);
  }
}

/** The old baseball cap: a dome and a front brim. */
export function buildCap(color: string): THREE.Group {
  const cap = new THREE.Group();
  cap.name = "cap";
  const mat = toon(color);
  const dome = inked(new THREE.SphereGeometry(0.21, facets(20), facets(10, 2), 0, Math.PI * 2, 0, Math.PI / 2), mat, 0.015);
  dome.position.y = 0.04;
  cap.add(dome);
  const brim = inked(new THREE.CylinderGeometry(0.16, 0.16, 0.025, facets(20), 1, false, -Math.PI / 2, Math.PI), mat, 0.012);
  brim.position.set(0, 0.06, 0.1);
  brim.scale.set(1, 1, 1.1);
  cap.add(brim);
  return cap;
}

/**
 * The toy's wide-brim cowboy hat (head space, face +z): a pinched crown, a
 * band, and a brim that curls up at the sides, with the toy's sideburns
 * showing under it. The natural face's is `hat-natural.ts`.
 */
export function buildCowboyHat(color: string, hair: string): THREE.Group {
  const hat = new THREE.Group();
  hat.name = "cowboy-hat";
  const felt = toon(color, { roughness: 0.75 });
  const crown = inked(new THREE.CylinderGeometry(0.15, 0.19, 0.17, facets(20)), felt, 0.015);
  crown.scale.set(0.95, 1, 1.1);
  crown.position.y = 0.23;
  hat.add(crown);
  // The pinch down the top of the crown.
  const dent = new THREE.Mesh(new THREE.SphereGeometry(0.1, facets(12), facets(8, 2)), felt);
  dent.scale.set(1.2, 0.35, 1.5);
  dent.position.y = 0.32;
  hat.add(dent);
  const band = new THREE.Mesh(new THREE.TorusGeometry(0.182, 0.014, facets(6, 2), facets(24)), toon("#2a1a12", { roughness: 0.6 }));
  band.rotation.x = Math.PI / 2;
  band.scale.set(1, 1.1, 1);
  band.position.y = 0.165;
  hat.add(band);
  // The brim: a lathed disc that turns up at its rim, stretched front to back.
  const brimPts = [
    new THREE.Vector2(0.17, 0),
    new THREE.Vector2(0.28, 0.005),
    new THREE.Vector2(0.34, 0.035),
    new THREE.Vector2(0.36, 0.07),
  ];
  const brim = inked(new THREE.LatheGeometry(brimPts, facets(28)), toon(color, { roughness: 0.75, side: THREE.DoubleSide }), 0.012);
  brim.name = "brim";
  brim.scale.set(0.9, 1, 1.08);
  brim.position.y = 0.15;
  hat.add(brim);
  for (const part of buildToyHatHair(hair)) hat.add(part);
  return hat;
}

/**
 * The toy's space helmet with its visor flipped up (head space, face +z): a
 * white shell open at the face, a neck ring, the gold visor over the brow,
 * and the toy's fringe and ponytail in the opening. The natural face's is
 * `helmet-natural.ts`.
 */
export function buildSpaceHelmet(color: string, hair: string): THREE.Group {
  const helmet = new THREE.Group();
  helmet.name = "space-helmet";
  const shellMat = toon(color, { roughness: 0.35, side: THREE.DoubleSide });
  // Leave a window about the face (+z, phi = pi/2 in three's sphere).
  const open = 1.9;
  // Down past the jaw, so it seats on the neck ring (#80).
  const shell = inked(new THREE.SphereGeometry(0.265, facets(32), facets(20, 2), Math.PI / 2 + open / 2, Math.PI * 2 - open, 0, Math.PI * 0.86), shellMat, 0.015);
  shell.position.y = 0.02;
  helmet.add(shell);
  const top = inked(new THREE.SphereGeometry(0.265, facets(32), facets(12, 2), 0, Math.PI * 2, 0, Math.PI * 0.2), shellMat, 0.015);
  top.position.y = 0.02;
  helmet.add(top);
  const ring = inked(new THREE.TorusGeometry(0.2, 0.035, facets(10, 2), facets(28)), toon("#9aa3b5", { roughness: 0.3, metalness: 0.6 }, "metal"), 0.012);
  ring.rotation.x = Math.PI / 2;
  // Down on the suit's collar, under the taller neck (#80).
  ring.position.y = -0.24;
  helmet.add(ring);
  // The visor, up: a gold cap tipped back over the brow.
  const visor = new THREE.Mesh(
    new THREE.SphereGeometry(0.275, facets(24), facets(10, 2), Math.PI / 2 - open / 2, open, Math.PI * 0.1, Math.PI * 0.2),
    toon("#e3b341", { roughness: 0.15, metalness: 0.8, side: THREE.DoubleSide }, "metal"),
  );
  visor.name = "visor";
  visor.position.y = 0.02;
  helmet.add(visor);
  for (const part of buildToyHelmetHair(hair)) helmet.add(part);
  return helmet;
}
